// Grammar Units 9 Data - Unit 9: Gerunds Complete Learning Course
// Based on Ch09 (1st Half).jsx and Ch09 (2nd Half).jsx
// Organized for Supabase migration and systematic data management

export const grammarUnits9Data = [
  {
    id: 9,
    title: { traditional: '動名詞完全學習課程', simplified: '动名词完全学习课程', english: 'Gerunds Complete Learning Course' },
    subtitle: { traditional: '掌握動名詞的四大功能', simplified: '掌握动名词的四大功能', english: 'Master the four main functions of gerunds' },
    description: { traditional: '系統性學習英文動名詞，包括名詞、形容詞、副詞功能和特殊用法。', simplified: '系统性学习英文动名词，包括名词、形容词、副词功能和特殊用法。', english: 'Systematic learning of English gerunds including noun, adjective, adverb functions and special usage.' },
    icon: '📚',
    color: 'from-purple-500 to-violet-600',
    status: 'notStarted',
    difficulty: 'intermediate',
    estimatedHours: 4,
    content: {
      overview: {
        title: "Overview - 動名詞概述",
        icon: "📚",
        content: {
          formation: {
            title: "Formation 形成方式",
            content: "動名詞 = 原形動詞 + -ing = V-ing",
            note: "動名詞具有動詞性質的名詞，與不定詞一樣，沒有人稱、時態的區別，作不可數用。"
          },
          functions: [
            "1-1 作主詞（後面接單數動詞）",
            "1-2 作補語（用來補充說明主詞、受詞）", 
            "1-3 動詞的受詞（某些動詞之後須用動名詞，以作為受詞用）",
            "1-4 介系詞的受詞（多數的介系詞之後接動名詞，以作為受詞用）"
          ]
        }
      },
      activities: {
        title: "Expressing Activities - 表示活動的動名詞",
        icon: "🏃",
        subsections: [
          {
            title: "2-1 go + V-ing 表示「從事某種活動」",
            content: {
              activities: [
                { english: "boating", chinese: "划船" },
                { english: "bowling", chinese: "保齡球" },
                { english: "camping", chinese: "露營" },
                { english: "canoeing", chinese: "划獨木舟（平台式、單槳槳）" },
                { english: "dancing", chinese: "跳舞" },
                { english: "diving", chinese: "跳水" },
                { english: "fishing", chinese: "釣魚" },
                { english: "hang gliding", chinese: "滑翔翼" },
                { english: "hiking", chinese: "爬山" },
                { english: "hunting", chinese: "打獵" },
                { english: "horseback riding", chinese: "騎馬" },
                { english: "in-line skating", chinese: "溜直排輪" },
                { english: "jogging", chinese: "慢跑" },
                { english: "kayaking", chinese: "划獨木舟（封閉式、雙槳槳）" },
                { english: "paragliding", chinese: "飛行傘運動" },
                { english: "rock climbing", chinese: "攀岩" },
                { english: "running", chinese: "跑步" },
                { english: "sailing", chinese: "帆船運動" },
                { english: "scuba diving", chinese: "潛水" },
                { english: "shopping", chinese: "購物" },
                { english: "sightseeing", chinese: "觀光" },
                { english: "skating", chinese: "溜冰" },
                { english: "skateboarding", chinese: "滑板運動" },
                { english: "skiing", chinese: "滑雪" },
                { english: "snorkelling", chinese: "浮潛" },
                { english: "snowboarding", chinese: "滑雪板運動" },
                { english: "swimming", chinese: "游泳" },
                { english: "surfing", chinese: "衝浪" },
                { english: "white-water rafting", chinese: "泛舟" },
                { english: "windsurfing", chinese: "風浪板運動" }
              ]
            }
          },
          {
            title: "2-2 do some/a lot of/a little + V-ing",
            content: {
              explanation: "表示從事某種活動，日活動不限於戶外活動",
              note: "do + V-ing 的用法中，do 後面一定要加上一些量詞，如：some、a lot of、much、a little、no",
              examples: [
                { english: "do some shopping", chinese: "買些東西" },
                { english: "do a lot of studying", chinese: "大量學習" },
                { english: "do a little cleaning", chinese: "做些清潔工作" },
                { english: "I like to do some painting on weekends.", chinese: "我喜歡週末的時候畫畫。" },
                { english: "My mother does a little exercising every day after work.", chinese: "我媽媽每天下班後做一點運動。" }
              ]
            }
          }
        ]
      },
      adjective_gerund: {
        title: "Adjective + Preposition + V-ing - 形容詞+介系詞+V-ing",
        icon: "✨",
        content: {
          patterns: [
            { phrase: "afraid of", meaning: "害怕" },
            { phrase: "good at", meaning: "擅長" },
            { phrase: "bad at", meaning: "不擅長" },
            { phrase: "capable of", meaning: "有能力" },
            { phrase: "excited about", meaning: "對...興奮" },
            { phrase: "fed up with", meaning: "厭倦" },
            { phrase: "fond of", meaning: "喜愛" },
            { phrase: "interested in", meaning: "對...有興趣" },
            { phrase: "pleased about", meaning: "對...高興" },
            { phrase: "tired of", meaning: "厭倦" },
            { phrase: "sad about", meaning: "對...傷心" },
            { phrase: "satisfied/content with", meaning: "對...滿意" }
          ],
          examples: [
            { english: "Elle was excited about getting accepted to Harvard.", chinese: "艾兒對獲准入學哈佛感到很興奮。" },
            { english: "My mother is particularly good at painting landscapes.", chinese: "我媽媽特別擅長畫風景畫。" }
          ]
        }
      },
      verb_preposition: {
        title: "Verb + Preposition + V-ing - 動詞+介系詞+V-ing",
        icon: "🔗",
        content: {
          patterns: [
            { phrase: "apologize for", meaning: "為...道歉" },
            { phrase: "approve of", meaning: "贊成" },
            { phrase: "believe in", meaning: "相信" },
            { phrase: "care about", meaning: "關心" },
            { phrase: "decide against", meaning: "決定不" },
            { phrase: "devote to", meaning: "致力於" },
            { phrase: "dream of/about", meaning: "夢想" },
            { phrase: "feel like", meaning: "想要" },
            { phrase: "worry about", meaning: "擔心" },
            { phrase: "get away with", meaning: "逃脫懲罰" },
            { phrase: "get out of", meaning: "避免" },
            { phrase: "insist on", meaning: "堅持" },
            { phrase: "look forward to", meaning: "期待" },
            { phrase: "put up with", meaning: "忍受" },
            { phrase: "succeed in", meaning: "成功" },
            { phrase: "talk about", meaning: "談論" },
            { phrase: "think about", meaning: "考慮" },
            { phrase: "think of", meaning: "想到" }
          ],
          specialPatterns: [
            { phrase: "have a hand in", meaning: "參與" },
            { phrase: "have/take a crack at", meaning: "試圖" },
            { phrase: "have a knack for", meaning: "對...有天賦；擅長" },
            { phrase: "take advantage of", meaning: "善用；利用" },
            { phrase: "take care of", meaning: "照顧" }
          ],
          prepositionOnly: [
            { phrase: "despite", meaning: "儘管" },
            { phrase: "for fear of", meaning: "因害怕" },
            { phrase: "instead of", meaning: "代替" },
            { phrase: "in spite of", meaning: "儘管" }
          ],
          examples: [
            { english: "I'm looking forward to meeting your sisters.", chinese: "我很期待見到你的姊妹。" },
            { english: "The general insisted on trying the equipment himself.", chinese: "這位將軍堅持要自己試用這些裝備。" },
            { english: "Everyone in the office took a crack at solving the puzzle.", chinese: "辦公室裡的每個人都試圖要解開這個謎題。" },
            { english: "Joe had a hand in designing the plan.", chinese: "喬參與設計這項計畫。" },
            { english: "Instead of staying inside all weekend, we could go for a hike.", chinese: "我們可以到郊外走走，而不是整個週末都待在家裡。" },
            { english: "She wouldn't say anything for fear of embarrassing herself.", chinese: "她怕自己會出醜所以什麼也沒有說。" }
          ]
        }
      },
      common_verbs: {
        title: "Common Gerund Verbs - 其他常見的動詞",
        icon: "📝",
        subsections: [
          {
            title: "Regular Gerund Verbs",
            verbs: ["appreciate", "avoid", "consider", "deny", "enjoy", "miss", "mind", "regret", "resent", "risk"],
            examples: [
              { english: "They denied cheating on the test.", chinese: "他們否認考試作弊。" },
              { english: "The dog just avoided getting hit by the bus.", chinese: "這隻狗剛好避過一劫沒被公車撞上。" },
              { english: "Evan enjoys reading novels more than playing computer games.", chinese: "艾凡喜歡看小說勝過玩電腦遊戲。" },
              { english: "Tabitha resented having to spend the weekend with her grandparents.", chinese: "塔碧莎對於週末得要陪祖父母。" },
              { english: "Would you mind answering the door for me?", chinese: "你介意去幫我應門嗎？" },
              { english: "I don't want to risk shrinking my new shirt, so I won't put it in the dryer.", chinese: "我不想承擔新襯衫縮水的風險，所以我不會把它放進烘衣機裡。" }
            ]
          },
          {
            title: "Stop/Quit/Give up - 與「停止、放棄」的動詞",
            verbs: ["discontinue", "give up", "quit", "stop"],
            examples: [
              { english: "After years of poor sales, the company had to discontinue making that product.", chinese: "在幾年銷量不佳後，公司必須停止製造那項產品。" },
              { english: "Tom's health improved after he quit smoking.", chinese: "湯姆的健康狀況在他戒菸後大大改善。" }
            ]
          },
          {
            title: "Delay/Postpone - 與「延期、延遲、推遲」的動詞",
            verbs: ["defer", "delay", "postpone", "put off"],
            examples: [
              { english: "He put off visiting the dentist until he had a toothache.", chinese: "他一直推遲去看牙醫。" },
              { english: "When he found some papers were missing, he had to delay going on vacation.", chinese: "當他發現一些文件遺失而必須延後去度假。" }
            ]
          },
          {
            title: "Tolerance - 表「忍受、忍耐、忍不住」的動詞",
            verbs: ["bear", "resist", "stand", "tolerate", "can't/couldn't bear", "can't/couldn't help"],
            examples: [
              { english: "She couldn't bear watching the boxing match.", chinese: "她實在受不了看拳擊賽。" },
              { english: "On hearing the bad news, the girl could not help crying.", chinese: "一聽到那壞消息，那女孩忍不住大哭。" }
            ]
          },
          {
            title: "Memory - 表「想像、記憶」的動詞",
            verbs: ["imagine", "recall", "remember", "forget"],
            examples: [
              { english: "I remember meeting him at the conference, but I don't recall his name.", chinese: "我記得在會議上見過他，但我想不起他的名字。" },
              { english: "Can you imagine being a movie star?", chinese: "你可以想像當一個電影明星是怎麼樣嗎？" }
            ]
          },
          {
            title: "Requirements - 表「需要、要求」的動詞",
            verbs: ["entail", "call for", "involve", "require"],
            examples: [
              { english: "This course entails writing twenty essays.", chinese: "這門課需要寫二十篇短文。" },
              { english: "Craig's job involves spending long hours at the office.", chinese: "克雷格的工作需要長時間待在辦公室裡。" }
            ]
          }
        ]
      },
      functions: {
        title: "Functions of Gerunds - 動名詞的功用",
        icon: "⚙️",
        subsections: [
          {
            title: "1-1 作主詞",
            content: {
              rule: "V-ing + 單數動詞",
              note: "動名詞作主詞用時，之後須用單數動詞。",
              examples: [
                { english: "Since high school, dancing has been Jennifer's favorite activity.", chinese: "從中學起，跳舞就是珍妮佛最喜愛的活動。" },
                { english: "Flying at this time of night seems a little dangerous.", chinese: "在夜晚這個時候飛行似乎有點危險。" },
                { english: "Playing cards with friends is entertaining.", chinese: "跟朋友玩牌是令人愉悅的事。" }
              ]
            }
          },
          {
            title: "1-2 作補語",
            content: {
              patterns: ["S. + V. + V-ing (S.C.)", "S. + V. + O. + V-ing (O.C.)"],
              note: "動名詞在此作主詞補語或受詞補語。",
              examples: [
                { english: "Teaching is learning.", chinese: "教學相長。", type: "S.C." },
                { english: "Neil's favorite sport is swimming.", chinese: "尼爾最喜愛的運動是游泳。", type: "S.C." },
                { english: "He felt the fish bumping against his legs.", chinese: "他感覺到魚兒在碰撞他的腿。", type: "O.C." }
              ]
            }
          },
          {
            title: "1-3 作動詞的受詞",
            content: {
              note: "需接動名詞作為受詞的動詞包括：",
              verbs: ["begin", "complete", "continue", "finish", "keep", "keep on", "start"],
              examples: [
                { english: "After she finished writing her essay, she read it carefully to check for mistakes.", chinese: "她在寫完短文之後，仔細地重複檢查有沒有錯誤。" },
                { english: "If you keep walking straight for three miles, you'll get to the city.", chinese: "如果你繼續往前直走三英里，你就可以到城裡。" }
              ]
            }
          }
        ]
      },
      important_points: {
        title: "Important Points - 動名詞的注意要點",
        icon: "⚠️",
        content: [
          {
            title: "4-1 having + p.p. (動名詞的完成式與被動語態說明)",
            description: "表示動作發生在主要動詞之前"
          },
          {
            title: "4-2 與現在分詞的比較（比較兩者之間的用法）",
            description: "區分動名詞和現在分詞的不同用法"
          },
          {
            title: "4-3 兩個以上的動名詞主詞與動詞的搭配用法",
            description: "多個動名詞主詞時的動詞一致性"
          },
          {
            title: "4-4 接受格或所有格代名詞",
            description: "動名詞前的代名詞形式選擇"
          }
        ]
      },
      comparison: {
        title: "Infinitive vs Gerund - 不定詞與動名詞的比較",
        icon: "⚖️",
        content: [
          {
            title: "5-1 接不定詞或動名詞意義相同的動詞",
            description: "某些動詞後面可以接不定詞或動名詞，意思相同"
          },
          {
            title: "5-2 後接不定詞或動名詞，意思不同的動詞",
            description: "某些動詞後面接不定詞或動名詞時意思不同"
          }
        ]
      },
      common_mistakes: {
        title: "Common Mistakes - 易混淆用法",
        icon: "❌",
        content: [
          {
            title: "6-1 介系詞 to + V-ing 的慣用語",
            description: "某些片語中的to是介系詞，後面要接動名詞"
          },
          {
            title: "6-2 prefer 的用法",
            description: "prefer的不同用法和搭配"
          }
        ]
      },
      to_ving_confusing: {
        title: "容易混淆的用法 - to + V-ing",
        content: {
          patterns: [
            { pattern: 'in addition to', meaning: '除...之外', example: 'In addition to studying, he works part-time.' },
            { pattern: 'look forward to', meaning: '期待', example: 'I look forward to hearing from you.' },
            { pattern: 'be devoted to', meaning: '致力於', example: 'She is devoted to helping others.' },
            { pattern: 'be used to', meaning: '習慣於', example: 'I am not accustomed to walking long distances.' },
            { pattern: 'be opposed to', meaning: '反對', example: 'They are opposed to the new policy.' },
            { pattern: 'with a view to', meaning: '以...為目的', example: 'David worked with a view to earning more money.' },
            { pattern: 'according to', meaning: '根據', example: 'According to the report, sales increased.' },
            { pattern: 'pay attention to', meaning: '注意', example: 'Please pay attention to the instructions.' },
            { pattern: 'thanks to', meaning: '幸虧; 因為', example: 'Thanks to your help, we succeeded.' },
            { pattern: 'owing to', meaning: '因為', example: 'The game was cancelled owing to rain.' },
            { pattern: 'take to', meaning: '喜歡', example: 'She took to painting immediately.' },
            { pattern: 'be exposed to', meaning: '暴露於', example: 'Children are exposed to many influences.' }
          ]
        }
      },
      prefer_usage: {
        title: "prefer 的用法",
        content: {
          patterns: [
            { 
              pattern: 'prefer to + V', 
              meaning: '是不定詞',
              example: 'Jessica prefers to grow her own vegetables.',
              variations: [
                'prefer to + V + (rather) than + V',
                'would rather + V... than + V',
                'prefer to + V + instead of + V-ing'
              ]
            },
            { 
              pattern: 'prefer A to B', 
              meaning: '是介系詞',
              example: 'Howard prefers camping in a forest to staying in a hotel.'
            }
          ],
          comparison: '比較級',
          note: '寧願...而不要...; 比較喜歡...勝於...'
        }
      },
      try_mean: {
        title: "try 和 mean 的用法",
        content: {
          try: {
            'try + to + V': {
              meaning: '設法努力去做某事',
              example: 'I tried to find the information on the Internet for hours.'
            },
            'try + V-ing': {
              meaning: '試著去做某事',
              example: 'If you want more information, you might try surfing the Internet.'
            }
          },
          mean: {
            'mean + to + V': {
              meaning: '打算要去做',
              example: "He didn't mean to hurt your feelings."
            },
            'mean + V-ing': {
              meaning: '意味著、表示',
              example: 'Good manners means considering others\' needs before you speak or act.'
            }
          }
        }
      },
      remember_stop_forget: {
        title: "remember, stop, go on, regret 的用法",
        content: {
          remember: {
            'remember + to + V': {
              meaning: '記得要去做某事 (此事尚未被執行)',
              example: 'Did you remember to shut the door when you left?'
            },
            'remember + V-ing': {
              meaning: '記得曾做過某事 (此事已被執行)',
              example: 'I remember shutting the door when I left.'
            }
          },
          stop: {
            'stop + to + V': {
              meaning: '停下正在進行的動作，去執行另外一個動作',
              example: 'Mr. Duncan was lost, so he stopped to ask someone for help.'
            },
            'stop + V-ing': {
              meaning: '停止正在進行的動作',
              example: "I won't listen to you any more. Please stop talking."
            }
          },
          'go on': {
            'go on + to + V': {
              meaning: '(停下先前的動作) 繼續進行另一個動作，或程序中的下個步驟',
              example: 'After introducing himself, Professor Harkins went on to explain his syllabus.'
            },
            'go on + V-ing': {
              meaning: '繼續正在進行的動作',
              example: 'They went on talking for two hours.'
            }
          },
          regret: {
            'regret + to + V': {
              meaning: '遺憾要來告知、說 (此事尚未被執行)',
              example: 'We regret to inform you that you have been rejected by the committee.'
            },
            'regret + V-ing': {
              meaning: '後悔做了這件事 (此事已完成)',
              example: 'He regretted behaving so badly in front of his girlfriend\'s parents.'
            }
          }
        }
      },
      gerund_infinitive_comparison: {
        title: "不定詞和動名詞用法的比較",
        content: {
          similar: {
            title: '後接不定詞或動名詞，意思相同的動詞',
            verbs: ['begin', 'continue', 'hate', 'love', 'start', 'like', 'prefer'],
            note: '左列的動詞接動名詞或是不定詞，意思都相同',
            examples: [
              { verb: 'like', inf: 'I like to listen to music.', ger: 'I like listening to music.' },
              { verb: 'prefer', inf: 'Tony prefers to hike in the mountains.', ger: 'Tony prefers hiking in the mountains.' }
            ]
          },
          different: {
            title: '後接不定詞或動名詞，意思不同的動詞',
            verbs: ['forget', 'remember', 'stop', 'go on', 'regret', 'try', 'mean'],
            patterns: {
              'V-ing': '表示該動作已發生或正在進行',
              'to + V': '表示該動作尚未發生'
            }
          }
        }
      },
      gerund_usage_timing: {
        title: "動名詞的使用時機",
        content: {
          'common-phrases': {
            title: '與動名詞搭配的慣用語',
            phrases: [
              'have trouble/difficulty + V-ing (很費力、有困難)',
              'have a good/hard time + V-ing (玩得很愉快；過得不是很愉快)',
              "there's/it's no use + V-ing (無濟於事、是沒有用的)",
              "there's no point in + V-ing (是沒有用的)"
            ],
            examples: [
              'He had trouble (in) understanding their thick accent.',
              "There's no use crying over spilt/spilled milk.",
              "There's no point in asking Mark; he doesn't know, either."
            ]
          },
          'preposition-patterns': {
            title: '介系詞 + V-ing',
            patterns: [
              {
                prep: 'on/upon + V-ing',
                meaning: '當...、一...就',
                transform: 'when → at the moment (when) + 子句 / as soon as',
                example: 'On walking into the kitchen, Jessica smelled something burning.'
              },
              {
                prep: 'in + V-ing',
                meaning: '在...時候、期間',
                transform: 'while → during the time + 子句 / when',
                example: 'In preparing for my final exam, I met with some problems in math.'
              },
              {
                prep: 'without + V-ing',
                meaning: '沒有...',
                example: 'Without even taking off his shoes, Ted lay down on the bed and fell asleep.'
              }
            ]
          },
          'suggestion-patterns': {
            title: 'how about / what about + V-ing',
            meaning: '你認為...如何 (用於建議)',
            examples: [
              'How about having a cup of coffee now?',
              'What about inviting Tina to our wedding?'
            ]
          },
          'prevention-patterns': {
            title: '阻止 V. + sb + from + V-ing',
            verbs: ['keep', 'stop', 'prevent'],
            meaning: '阻止某人免於...; 某人無法...',
            examples: [
              'The heavy rain prevented me from coming to school on time.',
              'His manners kept him from complaining.'
            ]
          },
          'impossibility-patterns': {
            title: '表示「不可能」的句型',
            pattern: 'There is no + V-ing → It is impossible to + V.',
            meaning: '...是不可能的、不被允許的',
            examples: [
              'There is no living without air and water. → It is impossible to live without air and water.',
              'There is no telling what will happen in the future. → It is impossible to tell what will happen in the future.'
            ]
          },
          'busy-patterns': {
            title: '表示「忙於做某事」',
            pattern: 'be busy + (in) + V-ing / with + N.',
            meaning: 'busy 之後接 V-ing 或 with + 名詞',
            examples: [
              'Since the deadline is coming near, Howard is busy (in) doing his assignment.',
              'Since the deadline is coming near, Howard is busy with his assignment.'
            ]
          },
          'spend-cost-take': {
            title: '表示「花費」的句型',
            patterns: [
              {
                pattern: 'S. + spend + 時間/金額 + (in) V-ing / on + N.',
                note: '介系詞 in 大多省略',
                example: 'Martin spent much of his money (in) buying comic books.'
              },
              {
                pattern: 'It takes + sb + 時間/金額 + to + V.',
                note: '需要 (時間、金錢)',
                example: 'It took the workmen three hours to fix the gate.'
              },
              {
                pattern: 'It costs + sb + 金額 + to + V.',
                note: '花了某人...錢',
                example: 'It cost him NT$10,000 to buy that new cell phone.'
              }
            ]
          }
        }
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits9Data.find(unit => unit.id === unitId);
};

// Export units by difficulty
export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits9Data.filter(unit => unit.difficulty === difficulty);
};

// Export completed units
export const getCompletedUnits = () => {
  return grammarUnits9Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits9Data;
